import { useEffect, useState, useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Trophy, Flame, Medal, Globe, Star, Rocket } from "lucide-react";
import { PageWrap } from "@/components/PageWrap";
import { SectionHeading } from "@/components/SectionHeading";
import { PHOTOS } from "@/lib/assets";
import { getAchievements } from "@/lib/api";
import { useLang } from "@/i18n/LanguageContext";

const ICONS = { Trophy, Flame, Medal, Globe, Star, Rocket };

// Feature flag: achievements/trophies section temporarily hidden per client request
const SHOW_ACHIEVEMENTS = false;

const STORY_IMAGES = [PHOTOS.paddockTent, PHOTOS.wheelPaddock, PHOTOS.smokeBarrier];

export default function About() {
  const [achievements, setAchievements] = useState([]);
  const { t, pickField } = useLang();
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "25%"]);

  useEffect(() => {
    getAchievements().then(setAchievements).catch(() => {});
  }, []);

  const story = t("about.story");

  return (
    <PageWrap testId="about-page">
      {/* hero */}
      <section ref={heroRef} className="relative flex h-[64vh] min-h-[440px] items-end overflow-hidden">
        <motion.img
          src={PHOTOS.redbullArch}
          alt="Filip Vimpel"
          style={{ y: bgY }}
          className="absolute inset-0 h-[125%] w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#07080A] via-[#07080A]/45 to-[#07080A]/25" />
        <div className="scanlines absolute inset-0 opacity-50" />
        <div className="relative z-10 mx-auto w-full max-w-6xl px-4 pb-14 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.15 }}
          >
            <div className="font-mono text-xs uppercase tracking-[0.3em] text-[#B7FF2A]">// {t("about.kicker")}</div>
            <h1 data-testid="about-headline" className="mt-2 font-display text-6xl uppercase leading-[0.9] tracking-[0.03em] sm:text-8xl">
              {t("about.titlePrefix")}<span className="text-[#1EA7FF]">#606</span>
            </h1>
          </motion.div>
        </div>
      </section>

      {/* intro video — below hero, above the first story article */}
      <section className="mx-auto max-w-6xl px-4 pt-16 sm:px-6 lg:px-8 lg:pt-20">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          className="clip-corner relative overflow-hidden border border-white/10 bg-[#0F1217]"
          data-testid="about-intro-video"
        >
          <div className="aspect-video w-full">
            <iframe
              data-testid="about-intro-video-iframe"
              src="https://www.youtube.com/embed/zYC4V4H_Z4I"
              title="Filip Vimpel — intro video"
              className="h-full w-full"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              referrerPolicy="strict-origin-when-cross-origin"
              allowFullScreen
            />
          </div>
        </motion.div>
      </section>

      {/* story beats */}
      <section className="mx-auto max-w-6xl px-4 py-20 sm:px-6 lg:px-8">
        <div className="space-y-20 lg:space-y-28">
          {story.map((s, i) => (
            <div
              key={s.title}
              className={`grid grid-cols-1 items-center gap-8 lg:grid-cols-2 lg:gap-16 ${
                i % 2 === 1 ? "lg:[&>*:first-child]:order-2" : ""
              }`}
            >
              <motion.div
                initial={{ opacity: 0, x: i % 2 === 1 ? 40 : -40 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                className="clip-corner relative overflow-hidden"
              >
                <img src={STORY_IMAGES[i]} alt={s.title} className="aspect-[4/3] w-full object-cover" loading="lazy" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#07080A]/50 to-transparent" />
              </motion.div>
              <motion.div
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.55, delay: 0.15 }}
              >
                <div className="font-mono text-xs uppercase tracking-[0.3em] text-[#1EA7FF]">// {s.kicker}</div>
                <h2 className="mt-2 font-display text-4xl uppercase tracking-[0.04em] leading-[0.95] sm:text-5xl">{s.title}</h2>
                <p className="mt-5 leading-relaxed text-white/60">{s.text}</p>
              </motion.div>
            </div>
          ))}
        </div>
      </section>

      {/* achievements — temporarily hidden (set SHOW_ACHIEVEMENTS to true to re-enable) */}
      {SHOW_ACHIEVEMENTS && (
      <section className="mx-auto max-w-6xl px-4 py-20 sm:px-6 lg:px-8 lg:py-28">
        <SectionHeading kicker={t("about.trophiesKicker")} title={t("about.trophiesTitle")} accent="acid" testId="about-achievements-heading" />
        <div data-testid="about-achievements-grid" className="mt-10 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {achievements.map((a, i) => {
            const Icon = ICONS[a.icon] || Trophy;
            return (
              <motion.div
                key={a.id}
                data-testid={`achievement-card-${i}`}
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.5, delay: 0.08 * (i % 3), ease: [0.22, 1, 0.36, 1] }}
                className="light-sweep clip-corner group border border-white/10 bg-[#0F1217] p-6 transition-colors duration-200 hover:border-[#B7FF2A]/40"
              >
                <div className="flex items-center justify-between">
                  <div className="clip-corner-sm flex h-11 w-11 items-center justify-center bg-[#B7FF2A]/10 text-[#B7FF2A]">
                    <Icon className="h-5 w-5" />
                  </div>
                  <span className="font-mono text-2xl font-semibold text-white/15 transition-colors duration-300 group-hover:text-[#1EA7FF]/50">
                    {a.year}
                  </span>
                </div>
                <div className="mt-4 font-display text-2xl uppercase tracking-[0.04em] leading-tight">{pickField(a, "title")}</div>
                <p className="mt-2 text-sm leading-relaxed text-white/50">{pickField(a, "detail")}</p>
              </motion.div>
            );
          })}
        </div>
      </section>
      )}
    </PageWrap>
  );
}
